import React, { useEffect, useState } from "react";

/**
 * The frame every reveal plays inside.
 *
 * It is a fixed 820 x 820 stage, so an animation can be written in plain
 * pixels - an orbit of radius 300, a reel 125 tall - instead of
 * recomputing its geometry for every screen. On anything smaller the
 * whole stage is scaled down as one piece, which keeps the composition
 * identical on a phone and on the outlet's monitor.
 */

export const STAGE = 820;

function useStageScale() {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const fit = () => {
      const w = window.innerWidth - 24;
      const h = window.innerHeight - 24;
      setScale(Math.min(1, w / STAGE, h / STAGE));
    };
    fit();
    window.addEventListener("resize", fit);
    window.addEventListener("orientationchange", fit);
    return () => {
      window.removeEventListener("resize", fit);
      window.removeEventListener("orientationchange", fit);
    };
  }, []);

  return scale;
}

export default function RevealShell({
  title,
  status,
  remainingSeconds,
  children,
}) {
  const scale = useStageScale();
  // Minutes matter here: the draw overruns into the next round, whose
  // clock can read 292 - which "00:292" made a nonsense of.
  const total = Math.max(0, Number(remainingSeconds) || 0);
  const clock = `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;

  return (
    <div className="rv-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div
        className="rv-shell"
        style={{
          width: STAGE,
          height: STAGE,
          transform: `scale(${scale})`,
        }}
      >
        <div className="rv-top">
          <div className="rv-title">{title}</div>
          <div className="rv-timer">{clock}</div>
        </div>

        <div className="rv-stage">
          {/* The backdrop every draw plays against: slow rays, a gold ring
              with a light running round it, and a lit floor. Shared here
              so all six shows sit in the same room. */}
          <div className="rv-bg" aria-hidden="true">
            <div className="rv-bg-ambient" />
            <div className="rv-bg-rays" />
            <div className="rv-bg-ring" />
            <div className="rv-bg-ringlight" />
            <div className="rv-bg-floor" />
          </div>
          {children}
        </div>

        <div className="rv-status">{status}</div>
      </div>
    </div>
  );
}
